/**
 * HistoryList — table-ish list of past simulador sessions.
 *
 * Status-aware action button:
 *   active / paused      → "Continuar" → navigates back to /simulador
 *                          (resumeIfActive picks up via mount-time hook)
 *   close_won / close_lost / ended → "Ver relatório" → /simulador/relatorio/:id
 *   abandoned / expired  → muted, no action (transcript still viewable)
 */
import { Link, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import {
  ArrowRight,
  CheckCircle,
  Clock,
  Hourglass,
  Trophy,
  XCircle,
} from "@phosphor-icons/react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { DeleteEntityButton } from "@/components/shared/DeleteEntityButton";
import { BulkActionsBar } from "@/components/shared/BulkActionsBar";
import { Checkbox } from "@/components/ui/checkbox";
import { useDeleteSimulatorSession } from "../hooks/useDeleteSimulatorSession";
import { toast } from "@/lib/toast";
import { TYPE_ACCENT } from "@/features/decodificar/content";
import {
  DIFFICULTY_LABEL,
  ENEATYPE_NAMES,
  type SimulatorStatus,
} from "../types";
import type { SessionHistoryRow } from "../hooks/useSimulatorSessions";

export interface HistoryListProps {
  sessions: SessionHistoryRow[];
  isLoading?: boolean;
  onResume?: (sessionId: string) => Promise<void> | void;
}

function formatBRL(cents: number | null | undefined): string {
  if (cents == null) return "";
  return (cents / 100).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
    minimumFractionDigits: 2,
  });
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "";
  }
}

const STATUS_LABEL: Record<SimulatorStatus, string> = {
  active: "Em andamento",
  paused: "Pausado",
  ended: "Encerrado",
  close_won: "Fechou",
  close_lost: "Não fechou",
  abandoned: "Abandonado",
  expired: "Expirado",
};

function StatusBadge({ status }: { status: SimulatorStatus }) {
  if (status === "close_won") {
    return (
      <Badge className="bg-emerald-500/15 text-emerald-300 border-emerald-500/30 hover:bg-emerald-500/15">
        <Trophy weight="fill" className="w-3 h-3 mr-1" />
        Fechou
      </Badge>
    );
  }
  if (status === "close_lost") {
    return (
      <Badge variant="destructive">
        <XCircle weight="fill" className="w-3 h-3 mr-1" />
        Não fechou
      </Badge>
    );
  }
  if (status === "active" || status === "paused") {
    return (
      <Badge className="bg-sky-500/15 text-sky-300 border-sky-500/30 hover:bg-sky-500/15">
        <Hourglass weight="fill" className="w-3 h-3 mr-1" />
        {STATUS_LABEL[status]}
      </Badge>
    );
  }
  if (status === "ended") {
    return (
      <Badge variant="secondary">
        <CheckCircle weight="fill" className="w-3 h-3 mr-1" />
        Encerrado
      </Badge>
    );
  }
  return (
    <Badge variant="outline" className="text-muted-foreground">
      <Clock weight="fill" className="w-3 h-3 mr-1" />
      {STATUS_LABEL[status]}
    </Badge>
  );
}

export function HistoryList({ sessions, isLoading, onResume }: HistoryListProps) {
  const navigate = useNavigate();
  const deleteSession = useDeleteSimulatorSession();
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [selectionMode, setSelectionMode] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  useEffect(() => {
    setSelectedIds((prev) => {
      const validIds = new Set(sessions.map((s) => s.id));
      const next = new Set<string>();
      prev.forEach((id) => {
        if (validIds.has(id)) next.add(id);
      });
      return next;
    });
  }, [sessions]);

  function toggleSelection(id: string, checked: boolean) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  }

  async function handleBulkDelete() {
    if (selectedIds.size === 0) return;
    setBulkDeleting(true);
    const ids = Array.from(selectedIds);
    const results = await Promise.allSettled(ids.map((id) => deleteSession.mutateAsync(id)));
    const success = results.filter((r) => r.status === "fulfilled").length;
    const failed = results.length - success;
    if (success > 0) toast.success(`${success} simulação(ões) excluída(s).`);
    if (failed > 0) toast.error(`${failed} simulação(ões) não puderam ser excluídas.`);
    setSelectedIds(new Set());
    setBulkDeleting(false);
  }

  if (isLoading) {
    return (
      <Card className="p-6 bg-card/80 border-border/50 text-sm text-muted-foreground">
        Carregando histórico…
      </Card>
    );
  }
  if (sessions.length === 0) {
    return (
      <Card className="p-6 bg-card/80 border-border/50 text-center space-y-2">
        <p className="text-sm text-muted-foreground">
          Nenhuma simulação ainda.
        </p>
        <p className="text-xs text-muted-foreground/80">
          Clique em <strong>Nova simulação</strong> para começar.
        </p>
      </Card>
    );
  }

  return (
    <div className="space-y-2" data-testid="history-list">
      <BulkActionsBar
        totalCount={sessions.length}
        selectedCount={selectedIds.size}
        allSelected={sessions.length > 0 && selectedIds.size === sessions.length}
        selectionMode={selectionMode}
        deleting={bulkDeleting}
        entityPluralLabel="simulações"
        onToggleSelectionMode={(enabled) => {
          setSelectionMode(enabled);
          if (!enabled) setSelectedIds(new Set());
        }}
        onToggleSelectAll={(checked) => {
          if (!checked) {
            setSelectedIds(new Set());
            return;
          }
          setSelectedIds(new Set(sessions.map((s) => s.id)));
        }}
        onClearSelection={() => setSelectedIds(new Set())}
        onConfirmDelete={handleBulkDelete}
      />
      {sessions.map((s) => {
        const accent = TYPE_ACCENT[s.eneatype as keyof typeof TYPE_ACCENT];
        const eneatypeName = ENEATYPE_NAMES[s.eneatype];
        const personaName = s.persona_snapshot?.name ?? "Paciente";
        const procedureName = s.procedure_snapshot?.name ?? "(procedimento removido)";
        const procedureValue = formatBRL(s.procedure_snapshot?.value_cents ?? null);
        const isResumable = s.status === "active" || s.status === "paused";
        const isReportable =
          s.status === "close_won" ||
          s.status === "close_lost" ||
          s.status === "ended";

        return (
          <Card
            key={s.id}
            className="p-4 bg-card/80 border-border/50 hover:border-border/80 transition"
            data-testid={`history-row-${s.id}`}
          >
            <div className="flex items-start gap-3">
              {selectionMode ? (
                <div className="shrink-0 pt-1">
                  <Checkbox
                    checked={selectedIds.has(s.id)}
                    onCheckedChange={(v) => toggleSelection(s.id, Boolean(v))}
                    aria-label="Selecionar simulação"
                    disabled={bulkDeleting}
                  />
                </div>
              ) : null}
              {/* Eneatype dot */}
              <div
                className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold shrink-0 mt-0.5"
                style={{
                  background: `${accent}22`,
                  color: accent,
                  border: `1px solid ${accent}55`,
                }}
                aria-label={`Tipo ${s.eneatype}`}
              >
                {s.eneatype}
              </div>

              {/* Main info + actions */}
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                      <span className="text-sm font-semibold text-foreground">
                        {personaName}
                      </span>
                      <span className="text-xs text-muted-foreground hidden sm:inline">
                        Tipo {s.eneatype} ({eneatypeName})
                      </span>
                    </div>
                    <div className="mt-1 flex flex-wrap items-center gap-1.5">
                      <Badge
                        variant={s.difficulty === "hard" ? "destructive" : "secondary"}
                        className="text-[10px]"
                      >
                        {DIFFICULTY_LABEL[s.difficulty]}
                      </Badge>
                      <StatusBadge status={s.status} />
                      {s.final_score != null && (
                        <span className="text-xs font-semibold text-foreground tabular-nums">
                          {s.final_score}<span className="text-muted-foreground font-normal">/100</span>
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-muted-foreground mt-1 truncate">
                      {procedureName}
                      {procedureValue ? ` · ${procedureValue}` : ""} · {s.last_seq}{" "}
                      {s.last_seq === 1 ? "mensagem" : "mensagens"} ·{" "}
                      {formatDate(s.created_at)}
                    </div>
                  </div>
                  {/* Action */}
                  <div className="shrink-0 flex items-center gap-1">
                    <DeleteEntityButton
                      entityLabel="simulação"
                      confirmDescription="A simulação, mensagens e relatório serão removidos permanentemente."
                      onConfirm={async () => {
                        try {
                          await deleteSession.mutateAsync(s.id);
                          toast.success("Simulação excluída.");
                        } catch (err) {
                          toast.error(
                            err instanceof Error ? err.message : "Erro ao excluir simulação.",
                          );
                          throw err;
                        }
                      }}
                      disabled={bulkDeleting}
                      data-testid={`simulador-delete-${s.id}`}
                    />
                    {isResumable ? (
                      <Button
                        size="sm"
                        onClick={() => {
                          if (onResume) {
                            void onResume(s.id);
                            return;
                          }
                          navigate("/simulador");
                        }}
                        className="gap-1"
                        data-testid={`history-resume-${s.id}`}
                      >
                        Continuar
                        <ArrowRight weight="bold" className="w-3.5 h-3.5" />
                      </Button>
                    ) : isReportable ? (
                      <Button
                        asChild
                        size="sm"
                        variant="outline"
                        className="gap-1"
                        data-testid={`history-report-${s.id}`}
                      >
                        <Link to={`/simulador/relatorio/${s.id}`}>
                          Ver relatório
                          <ArrowRight weight="bold" className="w-3.5 h-3.5" />
                        </Link>
                      </Button>
                    ) : (
                      <span className="text-[11px] text-muted-foreground/70">
                        {STATUS_LABEL[s.status]}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </Card>
        );
      })}
    </div>
  );
}
